/**
 * 개인정보 가리기(privacy mask) 규칙 SSOT.
 *
 * "화면에 보이는 고객 개인정보를 가려서 보여주는 모드"의 규칙이 전부 여기 있다.
 * 값을 어떻게 가리는가(maskPii)와 어떤 항목이 개인정보인가(PII_KIND_BY_FIELD_KEY /
 * piiKindForCustomType)를 한 파일에서 결정한다 — 화면마다 제각각 가리면 어디는 가려지고
 * 어디는 노출되는 사고가 난다.
 *
 * ⚠️ 이름이 비슷한 formatMasks.ts(src/lib) 와 헷갈리지 말 것 — 그쪽은 "입력 형식화"
 *    (주민번호에 - 넣기, 카드번호 4자리 띄우기)이고, 가리는 일과 아무 상관이 없다.
 *
 * ⚠️ 이건 화면 가리개이지 접근 통제가 아니다. 값은 여전히 브라우저까지 내려오므로
 *    "직원에게 아예 안 보여준다"가 필요하면 서버가 값을 안 내려주는 별도 작업이 필요하다.
 *
 * 적용 범위는 **화면 읽기 표시만**이다. 서식 출력·내보내기·텍스트 변환 복사는 실제 업무
 * 산출물이라 원본 그대로 나간다(신청서에 홍*동이 찍히면 서류가 망가진다).
 */

import { FORM_FIELDS, type FormFieldInput } from "./form-field-keys.js";
import type { CustomFieldType } from "./field-registry.js";

// ─────────────────────────────────────────────────────────────────────────────
// 가릴 정보의 종류
// ─────────────────────────────────────────────────────────────────────────────

export type PiiKind =
  | "name" // 사람 이름 — 김**우 (가운데를 두 칸으로 덮어 길이까지 가린다)
  | "rrn" // 주민등록번호 — 9001**-*******
  | "birth" // 생년월일(6자리·YYYY-MM-DD) — 9001** / 1990-01-**
  | "phone" // 전화번호 — 010-95**-11**
  | "email" // 이메일 — ho****@gmail.com
  | "address" // 주소 — 부산 해운대구 우동 1**-**
  | "card" // 카드번호·유효기간 — 국민 1234-**-****
  | "account" // 계좌번호 — 은행명 + 첫 묶음만
  | "image"; // 신분증·카드 사진·서명 이미지 — 텍스트로 못 가려서 화면에서 흐리게 처리한다

/** 이미지류는 값(URL)을 문자열로 바꾸는 게 아니라 화면에서 흐리게 가린다. */
export function isImagePii(kind: PiiKind): boolean {
  return kind === "image";
}

// ─────────────────────────────────────────────────────────────────────────────
// 값 가리기
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 가림 글자는 `*` 다(2026-08-13 티켓 #461 — 그전에는 `•` 였다). 요청자가 화면에서 보고 싶다고
 * 지정한 모양이 `김**우`·`010-95**-11**` 라 그 글자를 그대로 쓴다 — 가려진 티가 분명해야
 * "이건 원본이 아니다"가 한눈에 읽힌다.
 */
const STAR = "*";

function repeat(n: number): string {
  return n > 0 ? STAR.repeat(n) : "";
}

/**
 * 홍길동 → 홍**동, 김철수영 → 김**우 자리처럼 **가운데는 항상 두 칸**으로 덮는다.
 * 실제 글자 수만큼 덮으면 이름 길이가 그대로 드러나므로 길이도 함께 가린다.
 * 2글자(김철) → 김*, 1글자는 가릴 게 없어 그대로.
 */
function maskName(v: string): string {
  const s = v.trim();
  if (s.length <= 1) return s;
  if (s.length === 2) return `${s[0]}${STAR}`;
  return `${s[0]}${STAR}${STAR}${s[s.length - 1]}`;
}

/**
 * 900101-1234567 → 9001**-*******
 * 연·월(앞 4자리)만 남긴다 — 연령대 확인은 매장의 일상 업무라 여기까지 가리면 가린 채로는
 * 아무 일도 못 하게 된다. 뒷자리(성별 포함)는 통째로 덮는다.
 */
function maskRrn(v: string): string {
  const digits = v.replace(/\D/g, "");
  if (digits.length < 6) return repeat(digits.length);
  const front = digits.slice(0, 6);
  const back = digits.slice(6);
  return `${front.slice(0, 4)}${repeat(2)}-${repeat(back.length || 7)}`;
}

/** 900101 → 9001**, 1990-01-01 → 1990-01-** (연·월까지만 남긴다 — 주민번호와 같은 선). */
function maskBirth(v: string): string {
  const s = v.trim();
  const hyphen = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (hyphen) return `${hyphen[1]}-${hyphen[2]}-${repeat(2)}`;
  const digits = s.replace(/\D/g, "");
  if (digits.length >= 6) return `${digits.slice(0, 4)}${repeat(2)}`;
  return repeat(digits.length);
}

/** 한 묶음의 앞 절반만 남기고 뒤를 덮는다(4자리 → 앞 2자리, 3자리 → 앞 1자리). */
function halfMasked(block: string): string {
  if (block.length <= 1) return repeat(block.length);
  const keep = Math.max(1, Math.floor(block.length / 2));
  return `${block.slice(0, keep)}${repeat(block.length - keep)}`;
}

/**
 * 010-9512-1134 → 010-95**-11**
 * 국번은 그대로 두고, 뒤 두 묶음은 **각각 앞 절반만** 남긴다. 뒤 4자리를 통째로 남기면
 * 그것만으로 고객에게 연락이 가능해져 가린 의미가 없고, 전부 덮으면 목록에서 사람을
 * 분간할 수 없다 — 그 사이를 고른 모양이다(티켓 #461 지정).
 */
function maskPhone(v: string): string {
  const digits = v.replace(/\D/g, "");
  if (digits.length < 7) return repeat(digits.length);
  const head = digits.startsWith("02") ? digits.slice(0, 2) : digits.slice(0, 3);
  const rest = digits.slice(head.length);
  const tail = rest.slice(-4);
  const middle = rest.slice(0, rest.length - 4);
  if (!middle) return `${head}-${halfMasked(tail)}`;
  return `${head}-${halfMasked(middle)}-${halfMasked(tail)}`;
}

/** hong@gmail.com → ho****@gmail.com (도메인은 남긴다). */
function maskEmail(v: string): string {
  const at = v.indexOf("@");
  if (at <= 0) return repeat(v.length);
  const id = v.slice(0, at);
  const domain = v.slice(at);
  const keep = id.slice(0, Math.min(2, id.length));
  return `${keep}${repeat(Math.max(id.length - keep.length, 2))}${domain}`;
}

/**
 * "부산 해운대구 우동 123-45" → "부산 해운대구 우동 1**-**"
 * 동네 이름까지는 남기고 **번지 숫자를 덮는다**(첫 숫자 한 칸만 남긴다) — 배송 권역·관할
 * 판단은 되고 집은 못 찾는 선. 숫자가 아예 없는 주소는 행정구역 두 덩이까지만 남긴다.
 */
function maskAddress(v: string): string {
  const s = v.trim();
  if (!s) return "";
  const firstDigit = s.search(/\d/);
  if (firstDigit >= 0) {
    const head = s.slice(0, firstDigit + 1);
    const tail = s.slice(firstDigit + 1).replace(/\d/g, STAR);
    return `${head}${tail}`;
  }
  const parts = s.split(/\s+/);
  if (parts.length <= 2) return s;
  return `${parts.slice(0, 2).join(" ")} ${repeat(2)}`;
}

/**
 * "국민 1234-56-789012" → "국민 1234-**-******", "1234 5678 9012 3456" → "1234 **** **** ****"
 * 카드사·은행 이름과 **첫 숫자 묶음**만 남기고 나머지 숫자를 덮는다. 구분기호(-·공백)는
 * 그대로 둬서 자릿수 모양이 유지되고, 매장이 "그 카드 맞나"를 앞자리로 대조할 수 있다.
 */
function maskLeadingGroupOnly(v: string): string {
  const first = v.match(/\d+/);
  if (!first || first.index === undefined) return v;
  // 구분기호 없이 붙여 쓴 번호("1234567890123456")는 전체가 한 묶음이라 그대로 두면 하나도
  // 안 가려진다 — 남기는 자릿수를 4자리로 못 박는다.
  const keptDigits = Math.min(first[0].length, 4);
  const end = first.index + keptDigits;
  return `${v.slice(0, end)}${v.slice(end).replace(/\d/g, STAR)}`;
}

/** 카드번호·카드 유효기간 — 첫 묶음만 남긴다. */
function maskCard(v: string): string {
  return maskLeadingGroupOnly(v);
}

/** 계좌번호 — 은행명 + 첫 묶음만 남긴다(은행명은 개인정보가 아니라 그대로). */
function maskAccount(v: string): string {
  return maskLeadingGroupOnly(v);
}

/** 종류별 가리기 규칙. Record 강제 — PiiKind 를 늘리면 여기를 채워야 컴파일된다. */
const MASKERS: Record<PiiKind, (v: string) => string> = {
  name: maskName,
  rrn: maskRrn,
  birth: maskBirth,
  phone: maskPhone,
  email: maskEmail,
  address: maskAddress,
  card: maskCard,
  account: maskAccount,
  image: () => "", // 이미지는 문자열로 가리지 않는다(화면에서 흐리게) — 호출되면 빈 값.
};

/**
 * 값이 비었음을 뜻하는 표시 문자열들. 화면은 빈 칸을 "—" 로 그리는데, 이걸 가릴 값으로
 * 오해하면 "— ••••" 나 "•" 같은 괴상한 글자가 나온다(2026-08-13 검증에서 실제로 나왔다).
 */
const EMPTY_PLACEHOLDERS = new Set([
  "—",
  "–",
  "-",
  "미입력",
  "없음",
  "(없음)",
  "N/A",
  "n/a",
]);

/** 가릴 내용이 실제로 있는가 — 글자도 숫자도 없으면(기호뿐이면) 가릴 게 없는 것이다. */
function hasNothingToMask(s: string): boolean {
  const t = s.trim();
  if (t === "") return true;
  if (EMPTY_PLACEHOLDERS.has(t)) return true;
  return !/[\p{L}\p{N}]/u.test(t);
}

/**
 * 개인정보 값 하나를 가린 문자열로. 이미 표시용으로 포맷된 문자열(하이픈 포함)을 넣어도
 * 되고 날것을 넣어도 된다 — 숫자만 뽑아 다시 조립하므로 결과 형태가 같다.
 *
 * 빈 값·빈 값 표시("—" 등)는 손대지 않고 그대로 돌려준다.
 */
export function maskPii(value: unknown, kind: PiiKind): string {
  if (value === null || value === undefined) return "";
  const s = typeof value === "string" ? value : String(value);
  if (hasNothingToMask(s)) return s;
  return MASKERS[kind](s);
}

// ─────────────────────────────────────────────────────────────────────────────
// 항목이 아닌 자유 글(메모) 속의 개인정보
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 메모처럼 **항목이 아닌 자유 글** 속의 전화번호·주민번호를 가린다.
 *
 * 매장은 메모에 `이승준 010-8979-6369 아이폰13` 처럼 적는다. 항목 기반 규칙이 안 통하는
 * 자리라 숫자 모양으로 찾는다 — 숫자류만 건드리고 **이름은 손대지 않는다**(사람 이름과
 * 모델명·매장명을 구분할 방법이 없어 엉뚱한 글자를 가리게 된다).
 *
 * 티켓 #461 검증에서 실제로 잡혔다: 목록·상세를 다 가려 놨는데 메모 미리보기에 전화번호가
 * 날것으로 남아 있었다.
 */
export function maskFreeTextPii(value: string): string {
  if (!value) return value;
  return (
    value
      // 주민번호 — 13자리(하이픈 있든 없든). 전화번호보다 먼저 본다.
      .replace(/\b(\d{6})[-\s]?([1-4]\d{6})\b/g, (m) => maskPii(m, "rrn"))
      // 전화번호 — 010-1234-5678 · 01012345678 · 02-123-4567 등.
      .replace(/\b(0\d{1,2})[-.\s]?(\d{3,4})[-.\s]?(\d{4})\b/g, (m) => maskPii(m, "phone"))
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 어떤 항목이 개인정보인가 — 빌트인 항목
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 빌트인 폼 항목(form-field-keys.ts) 중 가릴 것들. 여기 없는 키는 가리지 않는다
 * (모델명·요금제·가입유형 같은 거래 정보는 가려봐야 업무만 막힌다).
 *
 * ⚠️ 새 개인정보 항목을 FORM_FIELDS 에 추가하면 여기에도 넣어야 한다 —
 *    아래 assertKnownFieldKeys 가 오타난 키는 잡아주지만, 빠뜨린 키는 못 잡는다.
 */
export const PII_KIND_BY_FIELD_KEY: Record<string, PiiKind> = {
  // 고객 본인
  name: "name",
  rrn: "rrn",
  birth: "birth",
  phone: "phone",
  emergencyPhone: "phone",
  email: "email",
  address: "address",
  // 배송 정보
  shippingAddress: "address",
  shippingRecipientName: "name",
  shippingRecipientPhone: "phone",
  // 결제·자동이체
  cardNumber: "card",
  cardExpiry: "card", // 유효기간도 카드 실물 정보다(티켓 #461 지정)
  accountNumber: "account",
  autopayInfo: "account", // 레거시 합본 한 줄(은행/카드 번호가 섞여 있다)
  autopayHolder: "name",
  autopayBirth6: "birth",
  autopayHolderPhone: "phone",
  // 법정대리인(미성년자 개통)
  guardianName: "name",
  guardianRrn: "rrn",
  guardianPhone: "phone",
  // 신청인
  applicantName: "name",
  // 이미지·자필 — 텍스트로 못 가려서 화면에서 흐리게 처리한다
  idPhoto: "image",
  cardPhoto: "image",
  signature: "image",
  nameSign: "image",
  noticeSign: "image",
  applicantSign: "image",
};

/**
 * 위 표의 키가 실제 FORM_FIELDS 에 있는 키인지 확인. 오타·삭제된 키가 남아 조용히
 * 무효가 되는 걸 막는다(개발 중에만 부르는 검사 — 반환값이 비어 있어야 정상).
 */
export function unknownPiiFieldKeys(): string[] {
  return Object.keys(PII_KIND_BY_FIELD_KEY).filter((k) => !FORM_FIELDS[k]);
}

/** 빌트인 항목 키 → 가릴 종류(가리지 않으면 null). */
export function piiKindForFieldKey(key: string): PiiKind | null {
  return PII_KIND_BY_FIELD_KEY[key] ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// 어떤 항목이 개인정보인가 — 매장이 추가한 커스텀 항목
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 커스텀 항목(field-registry)은 매장이 라벨을 마음대로 붙이므로 키로 판정할 수 없다.
 * 대신 **입력 타입**으로 판정한다 — 주민번호·카드·계좌 타입을 골라 만들었다면 그 값은
 * 개인정보다. Record 강제 — CustomFieldType 을 늘리면 "이건 가릴 것인가"를 채워야 컴파일된다.
 */
const PII_KIND_BY_CUSTOM_TYPE: Record<CustomFieldType, PiiKind | null> = {
  text: null, // 자유 입력 — 무엇이 들어올지 몰라 가리지 않는다(매장이 필요하면 rrn/phone 타입으로 만든다)
  textarea: null,
  number: null,
  date: null,
  select: null,
  multiselect: null,
  email: "email",
  phone: "phone",
  card: "card",
  rrn: "rrn",
  account: "account",
  url: null,
  address: "address",
  postcode: null, // 우편번호만으로는 개인을 특정하지 못한다
  image: "image",
  catalog: null, // 모델·요금제 카탈로그 선택 — 거래 정보
  calc: null,
};

/** 커스텀 항목 타입 → 가릴 종류(가리지 않으면 null). */
export function piiKindForCustomType(type: CustomFieldType): PiiKind | null {
  return PII_KIND_BY_CUSTOM_TYPE[type] ?? null;
}

/**
 * 입력 위젯 종류(FormFieldInput)로 판정. 화면에 따라 커스텀 항목도 빌트인과 같은
 * FormFieldDef 모양(input 만 있고 CustomFieldType 은 사라진 형태)으로 내려오는 곳이
 * 있어서, 그런 자리에서도 가릴 수 있게 한 갈래를 더 둔다.
 * Record 강제 — FormFieldInput 을 늘리면 여기를 채워야 컴파일된다.
 */
const PII_KIND_BY_INPUT: Record<FormFieldInput, PiiKind | null> = {
  text: null, // 이름·계좌번호도 text 라 판정 불가 — 그런 항목은 키(PII_KIND_BY_FIELD_KEY)로 잡는다
  tel: "phone",
  email: "email",
  rrn: "rrn",
  address: "address",
  signature: "image",
  segmented: null,
  chip: null,
  autocomplete: null,
  file: "image",
  card: "card",
  expiry: "card", // 카드 유효기간 — 카드번호와 함께 가린다(티켓 #461)
  number: null,
  date: null,
  textarea: null,
  select: null,
  multiselect: null,
};

/** 입력 위젯 종류 → 가릴 종류(가리지 않으면 null). */
export function piiKindForInput(input: FormFieldInput): PiiKind | null {
  return PII_KIND_BY_INPUT[input] ?? null;
}

/**
 * 이 항목을 무엇으로 볼 것인가 — 키를 먼저 보고(빌트인 이름·계좌처럼 입력 타입만으론
 * 알 수 없는 것들), 없으면 입력 위젯, 그것도 없으면 커스텀 타입 순으로 판정한다.
 */
export function resolvePiiKind(opts: {
  key?: string;
  input?: FormFieldInput;
  type?: CustomFieldType;
}): PiiKind | null {
  if (opts.key) {
    const byKey = piiKindForFieldKey(opts.key);
    if (byKey) return byKey;
  }
  if (opts.input) {
    const byInput = piiKindForInput(opts.input);
    if (byInput) return byInput;
  }
  if (opts.type) return piiKindForCustomType(opts.type);
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// 편의 함수
// ─────────────────────────────────────────────────────────────────────────────

/**
 * 빌트인 항목 값을 화면 표시용으로 가린다. 가릴 항목이 아니거나 모드가 꺼져 있으면
 * 원본을 그대로 돌려준다 — 호출부에서 if 를 쓰지 않게 하려는 형태다.
 */
export function maskFieldValueIfPii(
  key: string,
  display: string,
  enabled: boolean,
): string {
  if (!enabled) return display;
  const kind = piiKindForFieldKey(key);
  if (!kind || isImagePii(kind)) return display;
  return maskPii(display, kind);
}

/**
 * 항목이 어떤 모양으로 내려오든(키·입력위젯·커스텀타입) 알아서 판정해 가린다.
 * 화면들이 이 하나만 부르면 되도록 만든 진입점.
 */
export function maskDisplayValue(
  field: { key?: string; input?: FormFieldInput; type?: CustomFieldType },
  display: string,
  enabled: boolean,
): string {
  if (!enabled) return display;
  const kind = resolvePiiKind(field);
  if (!kind || isImagePii(kind)) return display;
  return maskPii(display, kind);
}

/** 커스텀 항목(매장이 추가한 항목) 값의 화면 표시를 가린다. 판정은 입력 타입으로. */
export function maskCustomValueIfPii(
  type: CustomFieldType,
  display: string,
  enabled: boolean,
): string {
  if (!enabled) return display;
  const kind = piiKindForCustomType(type);
  if (!kind || isImagePii(kind)) return display;
  return maskPii(display, kind);
}
